import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, InputGroup, Pagination, Table } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { adminApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import QuoteDetailsPanel from "../../components/QuoteDetailsPanel";
import {
  buildPageItems,
  filterAndSortQuotes,
  getQuoteCategories,
  paginateItems,
  QUOTE_PAGE_SIZES,
  QUOTE_SORT_OPTIONS,
} from "../../utils/adminQuotes";
import { TableSkeleton } from "../../components/Skeleton";
import "./ManagePages.css";

const QUOTE_STATUSES = [
  "pending",
  "reviewed",
  "quoted",
  "accepted",
  "rejected",
  "closed",
];

const STATUS_VARIANT = {
  pending: "secondary",
  reviewed: "info",
  quoted: "primary",
  accepted: "success",
  rejected: "danger",
  closed: "dark",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  category: "all",
  dateFrom: "",
  dateTo: "",
  sortBy: "newest",
};

const ManageQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.quotes();
      setQuotes(res.data.data.quotes || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load quote requests"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => getQuoteCategories(quotes), [quotes]);

  const filteredQuotes = useMemo(
    () => filterAndSortQuotes(quotes, filters),
    [quotes, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredQuotes, page, pageSize),
    [filteredQuotes, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.status !== "all" ||
    filters.category !== "all" ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setExpandedId(null);
  };

  const changeStatus = async (quote, status) => {
    setSavingId(quote._id);
    try {
      const res = await adminApi.updateQuote(quote._id, { status });
      const updated = res.data.data.quote;
      setQuotes((prev) =>
        prev.map((q) => (q._id === updated._id ? { ...q, status: updated.status } : q))
      );
      toast.success("Quote updated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not update quote"));
    } finally {
      setSavingId(null);
    }
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Quote requests</h1>
          <p>Search, filter, and follow up on pricing requests from the website.</p>
        </div>
        <span className="manage-count">
          {filteredQuotes.length === quotes.length
            ? `${quotes.length} total`
            : `${filteredQuotes.length} of ${quotes.length}`}
        </span>
      </header>

      {!loading && quotes.length > 0 && (
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search reference, customer, category, message…"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="all">All statuses</option>
            {QUOTE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter"
            value={filters.category}
            onChange={(e) => updateFilter("category", e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
          />
          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
          />

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
          >
            {QUOTE_SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {QUOTE_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button variant="outline-secondary" size="sm" className="manage-clear-btn" onClick={clearFilters}>
              <FaTimes /> Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} cols={5} />
      ) : quotes.length === 0 ? (
        <p className="manage-empty">No quote requests yet.</p>
      ) : filteredQuotes.length === 0 ? (
        <div className="manage-empty">
          <p>No quotes match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Customer</th>
                  <th>Category</th>
                  <th>Received</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((quote) => {
                  const isExpanded = expandedId === quote._id;
                  return (
                    <React.Fragment key={quote._id}>
                      <tr>
                        <td>
                          <code>{quote.quoteNumber || quote._id.slice(-8)}</code>
                          <Button
                            variant="link"
                            size="sm"
                            className="manage-expand-btn"
                            onClick={() =>
                              setExpandedId(isExpanded ? null : quote._id)
                            }
                          >
                            {isExpanded ? (
                              <>
                                <FaChevronUp /> Hide
                              </>
                            ) : (
                              <>
                                <FaChevronDown /> Details
                              </>
                            )}
                          </Button>
                        </td>
                        <td>
                          <strong>{quote.name}</strong>
                          <div className="manage-muted">{quote.email}</div>
                          <div className="manage-muted">{quote.phone}</div>
                        </td>
                        <td>
                          {quote.category}
                          {quote.quantity && (
                            <div className="manage-muted">Qty: {quote.quantity}</div>
                          )}
                        </td>
                        <td>{new Date(quote.createdAt).toLocaleDateString()}</td>
                        <td className="text-nowrap">
                          <Badge bg={STATUS_VARIANT[quote.status] || "secondary"} className="me-2">
                            {quote.status}
                          </Badge>
                          <Form.Select
                            size="sm"
                            className="manage-select"
                            value={quote.status}
                            disabled={savingId === quote._id}
                            onChange={(e) => changeStatus(quote, e.target.value)}
                          >
                            {QUOTE_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="manage-order-details-row">
                          <td colSpan={5}>
                            <QuoteDetailsPanel quote={quote} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="manage-pagination-bar">
            <p className="manage-pagination-meta">
              Showing {pagination.showingFrom}–{pagination.showingTo} of {pagination.total}{" "}
              quotes
            </p>
            {pagination.totalPages > 1 && (
              <Pagination className="manage-pagination mb-0">
                <Pagination.Prev
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                {pageItems.map((item, index) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === pagination.page}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(pagination.totalPages, p + 1))
                  }
                />
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ManageQuotes;
